// AmbientDetailStage — the Set10 ambient-detail (sub-lattice band) uniform declarations +
// JS value helper.  Mirror of LightingStage.ts: the GLSL decls the assembler emits, plus the
// JS-side per-config value computation (the renderer sets these once per shading-config change
// alongside the light rig; `u_ambCellSize` is per-chunk and set with `u_origin`/`u_chunkSize`).
//
// Plan: Set10_BandLimitedDetailNormal.md §4.4.  The uniforms feed `ambientDetailNormal(p)` in
// Primitives/AmbientDetail.glsl.ts — the band-limited sub-lattice ambient octaves evaluated at
// PIXEL scale (geometry carries the scales the mesh can resolve; the material carries the scales
// below that; the crossover follows the zoom).

/** GLSL: the Set10 ambient-detail uniforms consumed by the primitive (declared ALWAYS — the
 *  assembler emits `ambientDetailNormal` into every eval, and the unlit path's
 *  `matAmbientSlopeScale()` indirection falls back to 1.0 because `u_slopeScale` only exists
 *  with lighting — the matGeoNormal()/matLightDir() precedent). */
export const AMBIENT_DETAIL_UNIFORM_DECLS = `
uniform float u_ambCellSize;       // the chunk's height-lattice spacing (chunkSize / (CHUNK_N-1)) — the residual's lattice-gradient tap spacing (Set10 residual design)
uniform float u_ambWavelength;     // TerrainAmbient.wavelength — octave 0 wavelength (m)
uniform float u_ambLacunarity;     // TerrainAmbient.lacunarity
uniform float u_ambPersistence;    // TerrainAmbient.persistence
uniform float u_ambAmplitude;      // TerrainAmbient.amplitude (master gain)
uniform float u_ambDetailStrength; // TerrainAmbient.detailNormalStrength ?? 1 (Set10 §4.3 — 0 disables the band EVERYWHERE: the CPU evalD gate AND this residual)
uniform int   u_ambOctaves;        // TerrainAmbient.octaves (clamped to the loop cap 8, §5.2)
uniform int   u_ambDetailOctaves;  // Set10 residual — the number of configured octaves with λ <= maxWavelengthAsDetail (the D band). o < this → residual; between this and u_ambOctaves → L (lattice-only, skipped); o >= u_ambOctaves → continuation (pixel-only)
uniform int   u_ambFractalType;    // 0 fbm / 1 ridged / 2 billow / 3 turbulence (matches Fbm.glsl modes)
uniform vec2  u_ambOffset;         // TerrainAmbient.offset_x/offset_y
uniform float u_ambSeed;           // terrainDefinition.seed (per-octave seeding, §5.2)
uniform sampler2D u_ambientWeightMask;   // issue 2 (2026-08-08): per-chunk ambient-weight multiplier lattice (R32F, CHUNK_N×CHUNK_N — the worker's closure, innermost closed-ring wins).  ambientDetailNormal multiplies ∇D by this, so 0-multiplier coastline rings silence the pixel-scale D like the CPU silences L + D·w.
uniform int u_ambientWeightMaskEnabled;  // 1 = sample the mask; 0 = no-op (no multiplier-bearing ring → byte-identical to pre-issue-2)
`;

/** Set10 — the shader octave loop cap (the primitive's GLSL `AMBIENT_OCTAVE_LOOP_CAP`).
 *  MUST match the GLSL constant, and be >= the maximum configured octave count (the user's
 *  realistic config is 18 — they correctly want detail even when zoomed in close; the old 8
 *  cap would clamp `u_ambOctaves` and truncate the fractal at exactly the wrong end). */
export const AMBIENT_DETAIL_OCTAVE_CAP = 18;

/** Map a `fractalType` string to the int enum the primitive's mode transforms read
 *  (0 fbm / 1 ridged / 2 billow / 3 turbulence — the Fbm.glsl modes). */
export function ambientFractalTypeInt(fractalType: string): number {
	switch (fractalType) {
		case "ridged": return 1;
		case "billow": return 2;
		case "turbulence": return 3;
		default: return 0; // fbm
	}
}

/** Resolve the Set10 per-config uniform values from a `TerrainAmbient` (+ the terrain seed).
 *  Returns the values the renderer writes to the ambient uniforms on shading-config change.
 *  `octaves` is clamped to the loop cap (the plan §5.2: the shader band needs ≤ ~4-6 octaves;
 *  `u_ambOctaves` MUST be clamped to the primitive's loop bound or the comparison is UB).
 *  `detailOctaves` = the number of CONFIGURED octaves whose wavelength <= maxWavelengthAsDetail
 *  (the D band — the shader's residual applies to exactly these; the world-fixed L/D split,
 *  zoom-independent). */
export function ambientDetailUniformValues(ambient: {
	algorithm?: string; fractalType?: string; wavelength?: number; octaves?: number;
	persistence?: number; lacunarity?: number; offset_x?: number; offset_y?: number;
	amplitude?: number; detailNormalStrength?: number; maxWavelengthAsDetail?: number;
}, seed: number): {
	wavelength: number; lacunarity: number; persistence: number; amplitude: number;
	detailStrength: number; octaves: number; detailOctaves: number; fractalType: number;
	offset: [number, number]; seed: number;
} {
	void ambient.algorithm; // the noise-type mismatch is the Set10 §5.2 decision (a) — accepted
	const octaves = Math.max(0, Math.min(AMBIENT_DETAIL_OCTAVE_CAP, Math.round(ambient.octaves ?? 0)));
	const wavelength = ambient.wavelength ?? 1000;
	const lacunarity = ambient.lacunarity ?? 2.0;
	// Count the configured D octaves: octave i has λ = wavelength / lacunarity^i.  D = λ <= th
	// (th = maxWavelengthAsDetail ?? wavelength/2 — the SAME rule buildAmbientLDContext uses, so
	// the shader's residual membership matches the CPU's L/D split exactly).
	const th = ambient.maxWavelengthAsDetail ?? wavelength / 2;
	let detailOctaves = 0;
	let wl = wavelength;
	for (let i = 0; i < octaves; i++) {
		if (wl <= th) detailOctaves++;
		wl /= Math.max(lacunarity, 1.0001);
	}
	return {
		wavelength,
		lacunarity,
		persistence: ambient.persistence ?? 0.5,
		amplitude: ambient.amplitude ?? 0,
		detailStrength: ambient.detailNormalStrength ?? 1,
		octaves,
		detailOctaves,
		fractalType: ambientFractalTypeInt(ambient.fractalType ?? "fbm"),
		offset: [ambient.offset_x ?? 0, ambient.offset_y ?? 0],
		seed,
	};
}
